// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo } from 'react';
import { Table, TableBody, TableHeader, TableHead, TableCell, TableRow, Button } from '@carbon/react';
import { array, bool, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import uniqueId from 'lodash/uniqueId';
import { ChevronDown, Copy } from 'lucide-react';

import { StyledDataTableSkeleton } from 'Components/Order/OrderProducts';
import { NWShowMore } from 'Components/shared/NWShowMore/NWShowMore';
import useNumberUtils from 'Hooks/useNumberUtils';
import useToastNotifications from 'Hooks/useToastNotifications';
import useScreen from 'Hooks/useScreen';
import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';

import IconWarranty from 'Assets/images/warranty.svg';
import IconInvoice from 'Assets/images/pdf.svg';

const StyledContainer = styled.div`
  width: 100%;
  text-align: left;
  margin: 1rem 0;

  .title {
    color: ${ props => props.theme.colors.darkGray };
    font-size: 1.875rem;
    font-weight: 600;
    margin-bottom: 10px;
  }

  button.cds--btn {
    display: block;
    padding: 0;
    min-width: 100%;
    height: 40px;
    border: 1px solid #1F59A2;
    text-align: center;
    font-size: 0.875rem;
    line-height: 40px;
    color: #1F59A2;

    svg {
      margin-left: 5px;

      &.showAll {
        transform: rotate(180deg);
      }
    }
  }

  table {
    border: 1px solid ${ props => props.theme.products.colors.border };
    thead {
      th {
        &.series {
          width: 5%;
        }
        &.number {
          width: 15%;
        }
        &.creationDate {
          width: 15%;
        }
        &.dueDate {
          width: 25%;
        }
        &.valueWithoutVAT {
          width: 20%;
        }
        &.valueWithVAT {
          width: 10%;
        }
        &.valueTotal {
          width: 30%;
        }
        &.actions {
          width: 10%;
          text-align: right;
        }
        background-color: ${ props => props.theme.products.colors.addToFavorite };
        font-size: 1rem;
        color: ${ props => props.theme.colors.darkGray };
        font-weight: 600;
      }
    }
    tbody {
      background-color: transparent;
      tr:not(:last-child) {
        td {
          border-bottom: 1px solid ${ props => props.theme.products.colors.border };
        }
      }
      td {
        background-color: transparent;
        font-size: 0.875rem;
        border: 0;

        button {
          cursor: pointer;
          background: transparent;
          border: none;

          svg {
            height: 32px;
            width: 32px;
          }
        }

        &.actionsCell {
          text-align: right;
        }

        .copy {
          margin-left: 8px;
          cursor: pointer;
        }
        
        &.invoiceNumberMobile {
          display: none;
          background: #F5F7F9;
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    table {
      border: none;

      thead {
        tr {
          display: none;
        }
      }

      tbody {
        tr {
          display: flex;
          flex-direction: column;
          height: auto;
          margin-bottom: 20px;
          border: 1px solid #E2E2E2;

          &:not(:last-child) {
            td {
              border-bottom: none;
            }
          }

          &:hover td {
            border: none;
            background-color: #fff;
          }

          td {
            padding: 10px 15px;
            display: flex;
            text-align: left !important;

            &.invoiceNumber {
              display: none;
            }

            &.invoiceNumberMobile {
              display: flex;
            }
          }

          td:before {
            content: attr(data-label);
            min-width: 130px;
            font-weight: 600;
            text-transform: capitalize;
          }
        }
      }
    }
  }
`;

const OrderInvoices = memo(({
  invoices,
  isExternalReseller,
  onDownloadInvoiceAndWarranty,
  loading,
  showAllInvoices,
  setShowAllInvoices,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { width } = useScreen();
  const isMobile = width < 768;
  const { formatNumber, formatDate } = useNumberUtils();
  const toast = useToastNotifications();
  const analytics = useAnalytics();
  const headers = useMemo(
    () => {
      const mainHeaders = [
        {
          key: 'series',
          header: t('orders.invoiceColumns.series')
        },
        {
          key: 'number',
          header: t('orders.invoiceColumns.number')
        },
        {
          key: 'creationDate',
          header: t('orders.invoiceColumns.creationDate')
        },
        {
          key: 'dueDate',
          header: t('orders.invoiceColumns.dueDate')
        }
      ];

      return [
        ...mainHeaders,
        ...(!isExternalReseller ? [{
          key: 'valueWithoutVAT',
          header: t('orders.invoiceColumns.valueWithoutVAT')
        },
        {
          key: 'valueWithVAT',
          header: t('orders.invoiceColumns.valueWithVAT')
        }] : [{
          key: 'valueTotal',
          header: t('orders.invoiceColumns.valueTotal')
        }]),
        {
          key: 'actions',
          header: t('orders.invoiceColumns.actions')
        }
      ];
    },
    [isExternalReseller]
  );

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);
    analytics.track('invoice_code_clipboard');

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledContainer theme={ theme } className="orderInvoices">
      <p className="title">{ t('orders.invoices') }</p>
      { loading && (
        <StyledDataTableSkeleton
          theme={ theme }
          showHeader={ false }
          showToolbar={ false }
          headers={ headers }
          columnCount={ headers.length }
          rowCount={ 5 }
        />
      ) }
      { (!loading && !invoices.length)
        ? t('orders.noInvoices')
        : (
          <Table theme={ theme }>
            <TableHead theme={ theme }>
              <TableRow>
                { (headers).map(col => (
                  <TableHeader key={ col.key } className={ col.key }>
                    { col.header }
                  </TableHeader>
                )) }
              </TableRow>
            </TableHead>
            <TableBody>
              <NWShowMore showAll={ isMobile ? showAllInvoices : true }>
                { invoices.map(invoice => (
                  <TableRow key={ uniqueId('_invoice') }>
                    <TableCell className="invoiceNumberMobile" data-label={ headers[1].header }>{ invoice.invoiceNumber }<Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(invoice.invoiceNumber) } /></TableCell>
                    <TableCell data-label={ headers[0].header }>{ invoice.invoiceSeries }</TableCell>
                    <TableCell className="invoiceNumber" data-label={ headers[1].header }>{ invoice.invoiceNumber }<Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(invoice.invoiceNumber) } /></TableCell>
                    <TableCell data-label={ headers[2].header }>{ formatDate(invoice.dateCreated, { timeStyle: undefined }) }</TableCell>
                    <TableCell data-label={ headers[3].header }>{ formatDate(invoice.dueDate, { timeStyle: undefined }) }</TableCell>
                    <TableCell data-label={ headers[4].header }>{ formatNumber(invoice.value, { currency: invoice?.currency?.currencyId }) }</TableCell>
                    { !isExternalReseller && <TableCell data-label={ headers[5].header }>{ formatNumber(invoice.valueVatIncluded, { currency: invoice?.currency?.currencyId }) }</TableCell>}
                    <TableCell data-label={ headers[isExternalReseller ? 5 : 6].header }>
                      <button
                        type="button"
                        onClick={ () => onDownloadInvoiceAndWarranty(false, invoice.id) }
                      >
                        <IconWarranty />
                      </button>
                      <button
                        type="button"
                        onClick={ () => onDownloadInvoiceAndWarranty(true, invoice.id) }
                      >
                        <IconInvoice />
                      </button>
                    </TableCell>
                  </TableRow>
                )) }
              </NWShowMore>
            </TableBody>
          </Table>
        )}
      { (!loading && invoices.length > 1 && isMobile) && (
        <Button
          kind="ghost"
          onClick={ () => setShowAllInvoices(!showAllInvoices) }
        >
          { showAllInvoices ? t('orders.showLessInvoices') : t('orders.showAllInvoices') }
          <ChevronDown className={ showAllInvoices ? 'showAll' : '' } />
        </Button>
      ) }
    </StyledContainer>
  );
});

OrderInvoices.propTypes = {
  invoices: array,
  isExternalReseller: bool,
  onDownloadInvoiceAndWarranty: func,
  loading: bool,
  showAllInvoices: bool,
  setShowAllInvoices: func,
};
OrderInvoices.displayName = 'OrderInvoicesComponent';
export default OrderInvoices;
